import {CommonModule} from '@angular/common';
import {Component, Input, forwardRef} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {TuiHint, TuiIcon, TuiTextfield} from '@taiga-ui/core';
import {
  TuiCheckbox,
  TuiChevron,
  TuiDataListWrapper,
  TuiInputChip,
  TuiTooltip,
} from '@taiga-ui/kit';
import {ErrorMessageComponent} from '../../v1/error-component/error-message.component';
import {GateweeSkeletonComponent} from '../../v1/gatewee-skeleton/gatewee-skeleton.component';

export interface GateweeMultiSelectOption<T = unknown> {
  label: string;
  value: T;
  disabled?: boolean;
}

@Component({
  selector: 'gatewee-multi-select',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TuiCheckbox,
    TuiChevron,
    TuiDataListWrapper,
    TuiHint,
    TuiIcon,
    TuiInputChip,
    TuiTextfield,
    TuiTooltip,
    ErrorMessageComponent,
    GateweeSkeletonComponent,
  ],
  templateUrl: './gatewee-multi-select.component.html',
  styleUrls: ['./gatewee-multi-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GateweeMultiSelectComponent),
      multi: true,
    },
  ],
})
export class GateweeMultiSelectComponent<T = unknown>
  implements ControlValueAccessor
{
  @Input() label = '';
  @Input() placeholder = 'Selecione';
  @Input() items: GateweeMultiSelectOption<T>[] = [];
  @Input() loading = false;
  @Input() required = false;
  @Input() disabled = false;
  @Input() toolTip = '';
  @Input() iconStart = '';
  @Input() control?: FormControl | null;
  @Input() emptyStateText = 'Nenhuma opção encontrada';

  value: (T | GateweeMultiSelectOption<T>)[] = [];

  private onChange: (value: T[]) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: T[] | null): void {
    if (!Array.isArray(value) || value.length === 0) {
      this.value = [];
      return;
    }
    this.value = value.map(
      v =>
        this.items.find(o => this.compareValues(o.value, v)) ?? {
          label: String(v),
          value: v,
        },
    );
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onValueChange(value: (T | GateweeMultiSelectOption<T>)[]): void {
    this.value = Array.isArray(value) ? [...value] : [];
    this.onChange(this.getValuesAsT());
    this.markTouched();
  }

  private getValuesAsT(): T[] {
    return this.value
      .map(v => this.getSingleValue(v))
      .filter((v): v is T => v !== null && v !== undefined) as T[];
  }

  readonly stringify = (
    item: T | GateweeMultiSelectOption<T> | null,
  ): string => {
    if (item === null || item === undefined) return '';
    if (
      typeof item === 'object' &&
      'label' in item &&
      typeof (item as GateweeMultiSelectOption<T>).label === 'string'
    ) {
      return (item as GateweeMultiSelectOption<T>).label;
    }
    const found = this.items.find(option =>
      this.compareValues(option.value, item as T),
    );
    return found?.label ?? String(item);
  };

  readonly stringifyOption = (option: GateweeMultiSelectOption<T>): string =>
    option.label;

  readonly identityMatcher = (
    a: T | GateweeMultiSelectOption<T>,
    b: T | GateweeMultiSelectOption<T>,
  ): boolean =>
    this.compareValues(this.getSingleValue(a), this.getSingleValue(b));

  private isOption(
    v: T | GateweeMultiSelectOption<T> | null,
  ): v is GateweeMultiSelectOption<T> {
    return (
      v !== null &&
      typeof v === 'object' &&
      'value' in v &&
      (v as GateweeMultiSelectOption<T>).value !== undefined
    );
  }

  private getSingleValue(
    v: T | GateweeMultiSelectOption<T> | null | undefined,
  ): T | null | undefined {
    if (v === null || v === undefined) {
      return undefined;
    }
    if (this.isOption(v)) return v.value;
    return v;
  }

  get dropdownItems(): readonly GateweeMultiSelectOption<T>[] {
    return this.items;
  }

  isSelected(option: GateweeMultiSelectOption<T>): boolean {
    return this.value.some(v =>
      this.compareValues(this.getSingleValue(v), option.value),
    );
  }

  clearValue(): void {
    if (this.disabled) {
      return;
    }

    this.value = [];
    this.onChange([]);
    this.markTouched();
  }

  onInputMouseDown(event: MouseEvent): void {
    event.stopPropagation();
  }

  private markTouched(): void {
    this.onTouched();
    this.control?.markAsTouched();
  }

  private compareValues(
    a: T | null | undefined,
    b: T | null | undefined,
  ): boolean {
    if (a === b) {
      return true;
    }

    if (a && b && typeof a === 'object' && typeof b === 'object') {
      if ('id' in a && 'id' in b) {
        return a.id === b.id;
      }

      return JSON.stringify(a) === JSON.stringify(b);
    }

    return false;
  }
}
